import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { getCoverUrl, formatPostDate, readingTime, type Post } from "@/lib/blog";
import { ArrowLeft, ArrowRight, Search, BookOpen, Brain, LineChart, Cpu, Clock } from "lucide-react";
import logo from "@/assets/logo-abillyting-v2.jpeg.asset.json";

type PostWithCover = Post & { coverUrl: string | null };

const PILLARS = [
  { icon: Brain, title: "Inteligência Artificial aplicada", text: "Agentes, automações e IA que geram resultado no caixa — não hype." },
  { icon: LineChart, title: "Estratégia de crescimento", text: "Tráfego, conteúdo e conversão com método, dados e números reais." },
  { icon: Cpu, title: "Gestão & tecnologia", text: "PDV, finanças e processos que sustentam uma operação que escala." },
];

const Meta = ({ post }: { post: PostWithCover }) => (
  <p className="flex items-center gap-2 text-xs text-muted-foreground">
    <span>{formatPostDate(post.published_at)}</span>
    <span aria-hidden>·</span>
    <Clock size={12} /> <span>{readingTime(post.content)} min de leitura</span>
  </p>
);

const Cover = ({ post, className }: { post: PostWithCover; className: string }) =>
  post.coverUrl ? (
    <img src={post.coverUrl} alt={post.title} className={`${className} object-cover transition-transform duration-700 group-hover:scale-105`} loading="lazy" />
  ) : (
    <div className={`${className} bg-hero-gradient opacity-30`} />
  );

const Blog = () => {
  const [posts, setPosts] = useState<PostWithCover[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Blog Abillyting — Estratégia, IA e Crescimento";
    const load = async () => {
      const { data } = await supabase
        .from("posts" as never)
        .select("*")
        .eq("status", "published")
        .lte("published_at", new Date().toISOString())
        .order("published_at", { ascending: false });
      const rows = (data as unknown as Post[]) ?? [];
      setPosts(await Promise.all(rows.map(async (p) => ({ ...p, coverUrl: await getCoverUrl(p.cover_image) }))));
      setLoading(false);
    };
    load();
  }, []);

  const categories = useMemo(() => Array.from(new Set(posts.map((p) => p.category).filter(Boolean))) as string[], [posts]);
  const filtering = !!search || !!category;
  const q = search.toLowerCase();
  const filtered = posts.filter(
    (p) =>
      (p.title.toLowerCase().includes(q) || (p.excerpt ?? "").toLowerCase().includes(q)) &&
      (!category || p.category === category)
  );
  const featured = !filtering ? filtered[0] : undefined;
  const rest = featured ? filtered.slice(1) : filtered;

  return (
    <main className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/90 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/"><img src={logo.url} alt="Abillyting Marketing" className="h-14 w-auto object-contain" /></Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors font-medium">
            <ArrowLeft size={16} /> Voltar ao site
          </Link>
        </div>
      </header>

      {/* Hero editorial */}
      <section className="relative overflow-hidden border-b border-border">
        <div aria-hidden className="absolute -top-40 -right-32 w-[30rem] h-[30rem] rounded-full bg-primary/15 blur-3xl" />
        <div aria-hidden className="absolute -bottom-40 -left-32 w-[26rem] h-[26rem] rounded-full bg-accent/15 blur-3xl" />
        <div className="container mx-auto px-4 py-20 md:py-24 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-widest uppercase border border-primary/20">
              <BookOpen size={14} /> Abillyting Insights
            </span>
            <h1 className="font-heading font-bold text-4xl md:text-6xl leading-[1.08] mt-6 text-balance">
              Conhecimento de quem constrói{" "}
              <span className="bg-hero-gradient bg-clip-text text-transparent">crescimento real</span>.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mt-6 max-w-2xl text-balance">
              Análises profundas sobre estratégia de marketing, inteligência artificial e gestão — escritas por quem
              aplica isso todos os dias em operações reais. Sem fórmulas prontas, sem conteúdo raso.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4 mt-14">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                className="rounded-2xl border border-border bg-card/70 backdrop-blur p-5 hover:border-primary/40 transition-colors"
              >
                <p.icon className="text-primary mb-3" size={22} />
                <h2 className="font-heading font-semibold text-base">{p.title}</h2>
                <p className="text-sm text-muted-foreground mt-1">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-14 max-w-6xl">
        {/* Filtros */}
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-12">
          <div className="flex gap-2 flex-wrap">
            {[null, ...categories].map((c) => (
              <button
                key={c ?? "all"}
                onClick={() => setCategory(c)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  category === c
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                }`}
              >
                {c ?? "Todos os temas"}
              </button>
            ))}
          </div>
          <div className="relative w-full md:max-w-xs">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              placeholder="Buscar por tema ou palavra-chave"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-11 rounded-full border border-border bg-card pl-10 pr-4 text-sm outline-none transition-shadow focus:border-primary focus:ring-4 focus:ring-primary/15"
            />
          </div>
        </div>

        {loading && <p className="text-center text-muted-foreground py-16">Carregando artigos...</p>}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-16 max-w-md mx-auto">
            <BookOpen className="mx-auto text-primary mb-4" size={32} />
            <h3 className="font-heading text-xl font-semibold">
              {posts.length === 0 ? "Os primeiros estudos estão em produção" : "Nenhum artigo para essa busca"}
            </h3>
            <p className="text-muted-foreground mt-2 text-sm">
              {posts.length === 0
                ? "Estamos preparando análises completas. Em breve, publicações semanais por aqui."
                : "Tente outro termo ou explore todos os temas."}
            </p>
          </div>
        )}

        {/* Destaque */}
        {featured && (
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16">
            <Link to={`/blog/${featured.slug}`} className="group grid md:grid-cols-2 gap-8 items-center rounded-3xl border border-border bg-card overflow-hidden hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 transition-all">
              <div className="overflow-hidden h-full"><Cover post={featured} className="w-full h-full min-h-64 aspect-video md:aspect-auto" /></div>
              <div className="p-8 md:pr-10 space-y-4">
                <span className="text-xs font-semibold text-primary uppercase tracking-widest">
                  Em destaque{featured.category ? ` · ${featured.category}` : ""}
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold leading-tight group-hover:text-primary transition-colors">{featured.title}</h2>
                {featured.excerpt && <p className="text-muted-foreground line-clamp-3">{featured.excerpt}</p>}
                <Meta post={featured} />
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
                  Ler artigo completo <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          </motion.div>
        )}

        {rest.length > 0 && (
          <>
            {featured && <h2 className="font-heading text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-6">Últimas publicações</h2>}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {rest.map((post, i) => (
                <motion.article key={post.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}>
                  <Link to={`/blog/${post.slug}`} className="group flex flex-col h-full bg-card border border-border rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-primary/10 hover:border-primary/40 hover:-translate-y-1">
                    <div className="overflow-hidden"><Cover post={post} className="w-full aspect-video" /></div>
                    <div className="p-6 flex flex-col gap-3 flex-1">
                      {post.category && <span className="text-xs font-semibold text-primary uppercase tracking-wider">{post.category}</span>}
                      <h3 className="font-heading text-xl font-bold leading-snug group-hover:text-primary transition-colors">{post.title}</h3>
                      {post.excerpt && <p className="text-muted-foreground text-sm line-clamp-3">{post.excerpt}</p>}
                      <div className="mt-auto pt-2"><Meta post={post} /></div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </>
        )}
      </section>

      {/* CTA final */}
      <section className="container mx-auto px-4 pb-20 max-w-6xl">
        <div className="rounded-3xl bg-hero-gradient p-10 md:p-14 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary-foreground">Quer aplicar isso no seu negócio?</h2>
          <p className="text-primary-foreground/85 mt-3 max-w-xl mx-auto">
            Converse com nossa equipe e receba um diagnóstico de marketing e automação feito para a sua operação.
          </p>
          <Link to="/#contato" className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-background text-foreground font-semibold hover:gap-3 transition-all">
            Solicitar diagnóstico <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Blog;
